import { parseBoardStartDate } from "./timeDisplay.js";

const TOP_PLAYS_STATIC_URL = "/wp-content/plugins/waged-war/json/prize-picks-api-topplays.json";
const TOP_PLAYS_STATIC_SCHEMA_VERSION = 5;
const TOP_PLAYS_STATIC_MIN_SCHEMA_VERSION = 4;
const TOP_PLAYS_STATIC_TIMEOUT_MS = 1500;
const TOP_PLAYS_STATIC_MAX_LEAGUES = 3;
const TOP_PLAYS_STATIC_ROWS_PER_LEAGUE = 8;
const TOP_PLAYS_STATIC_PREFETCH_KEY = "__WW_TOP_PLAYS_PREFETCH";

let topPlaysStaticRequest = null;

const normalizeLeagueKey = (row) => String(
  row?._top_play_source_league || row?.league || ""
).trim().toLowerCase();

const normalizeLeagueList = (leagues) => (
  Array.isArray(leagues)
    ? Array.from(new Set(
      leagues
        .map((league) => String(league || "").trim().toLowerCase())
        .filter(Boolean)
    ))
    : []
);

const parseStartTime = (row) => {
  const raw = row?.start_time_iso || row?.start_time_obj || "";
  return raw ? parseBoardStartDate(raw) : null;
};

const emptyTopPlaysStaticCache = () => ({
  rows: [],
  sourceLeagues: [],
});

export const normalizeTopPlaysStaticCache = (payload, now = Date.now()) => {
  const schemaVersion = Number(payload?.schema_version);
  if (
    !payload ||
    typeof payload !== "object" ||
    !Number.isInteger(schemaVersion) ||
    schemaVersion < TOP_PLAYS_STATIC_MIN_SCHEMA_VERSION ||
    schemaVersion > TOP_PLAYS_STATIC_SCHEMA_VERSION ||
    !Array.isArray(payload.rows)
  ) {
    return emptyTopPlaysStaticCache();
  }

  const declaredSourceLeagues = normalizeLeagueList(payload.source_leagues);
  const sourceLeagues = (
    declaredSourceLeagues.length > 0
      ? declaredSourceLeagues
      : normalizeLeagueList(payload.rows.map(normalizeLeagueKey))
  ).slice(0, TOP_PLAYS_STATIC_MAX_LEAGUES);
  const allowedLeagues = new Set(sourceLeagues.slice(0, TOP_PLAYS_STATIC_MAX_LEAGUES));
  const leagueCounts = new Map();
  const rows = [];

  payload.rows.forEach((row) => {
    if (!row || typeof row !== "object") {
      return;
    }

    const league = normalizeLeagueKey(row);
    const startTime = parseStartTime(row);
    if (
      !league ||
      (allowedLeagues.size > 0 && !allowedLeagues.has(league)) ||
      !startTime ||
      startTime.getTime() <= now
    ) {
      return;
    }

    const leagueCount = leagueCounts.get(league) || 0;
    if (leagueCount >= TOP_PLAYS_STATIC_ROWS_PER_LEAGUE) {
      return;
    }

    leagueCounts.set(league, leagueCount + 1);
    rows.push(row);
  });

  return {
    rows,
    sourceLeagues,
  };
};

export const normalizeTopPlaysStaticPayload = (payload, now = Date.now()) => (
  normalizeTopPlaysStaticCache(payload, now).rows
);

const getTopPlaysStaticUrl = () => (
  typeof window !== "undefined" && window.__WW_TOP_PLAYS_STATIC_URL
    ? String(window.__WW_TOP_PLAYS_STATIC_URL)
    : TOP_PLAYS_STATIC_URL
);

const takePrefetchedRequest = (url) => {
  if (typeof window === "undefined") {
    return null;
  }

  const prefetch = window[TOP_PLAYS_STATIC_PREFETCH_KEY];
  if (
    !prefetch ||
    prefetch.consumed ||
    prefetch.url !== url ||
    !prefetch.promise ||
    typeof prefetch.promise.then !== "function"
  ) {
    return null;
  }

  prefetch.consumed = true;
  return prefetch;
};

const fetchTopPlaysStaticPayload = async () => {
  const url = getTopPlaysStaticUrl();
  const prefetch = takePrefetchedRequest(url);
  const controller = prefetch?.controller?.signal
    ? prefetch.controller
    : new AbortController();
  const startedAt = Number(prefetch?.startedAt);
  const elapsedMs = Number.isFinite(startedAt) ? Math.max(0, Date.now() - startedAt) : 0;
  const timeoutId = setTimeout(
    () => controller.abort(),
    Math.max(1, TOP_PLAYS_STATIC_TIMEOUT_MS - elapsedMs)
  );

  if (typeof window !== "undefined") {
    window.__WW_LAST_TOP_PLAYS_STATIC_URL = url;
  }

  try {
    const response = prefetch
      ? await prefetch.promise
      : await fetch(url, {
        cache: "force-cache",
        credentials: "same-origin",
        signal: controller.signal,
      });

    if (!response.ok) {
      return emptyTopPlaysStaticCache();
    }

    const cache = normalizeTopPlaysStaticCache(await response.json());
    if (typeof window !== "undefined") {
      window.__WW_BOARD_STAGE_TIMINGS = window.__WW_BOARD_STAGE_TIMINGS || {};
      window.__WW_BOARD_STAGE_TIMINGS.topPlaysReadyAt = Date.now();
    }
    return cache;
  } catch (error) {
    return emptyTopPlaysStaticCache();
  } finally {
    clearTimeout(timeoutId);
  }
};

export function FetchTopPlaysStaticCache() {
  if (!topPlaysStaticRequest) {
    topPlaysStaticRequest = fetchTopPlaysStaticPayload().then((cache) => {
      if (cache.rows.length === 0) {
        topPlaysStaticRequest = null;
      }

      return cache;
    });
  }

  return topPlaysStaticRequest;
}

export default function FetchTopPlaysStatic() {
  return FetchTopPlaysStaticCache().then((cache) => cache.rows);
}
